/**
 * The authored paths (docs/spec/02 §4, §7): the two ways into a formation and the challenging
 * stages' choreography. New curves in the 1981 game's character: cross and loop from the top,
 * circle in from a lower corner. Each is drawn for one side; `mirrored` gives the other.
 */

import { deg, DOWN } from './math';
import type { Path } from './path';
import { mirrorPath, PathBuilder } from './path';
import { FIELD } from './tuning';

const cache = new Map<string, Path>();

function once(name: string, make: () => Path): Path {
  let p = cache.get(name);
  if (!p) {
    p = make();
    cache.set(name, p);
  }
  return p;
}

export function mirrored(name: string, path: Path): Path {
  return once(`${name}/mirror`, () => mirrorPath(path, FIELD.w));
}

/**
 * From the top, right of centre: down, across to the left, a loop through the lower middle of the
 * screen and up. Its mirror crosses it.
 */
export function entryTop(): Path {
  return once('top', () =>
    new PathBuilder(128, -10, DOWN)
      .line(40)
      .arc(deg(35), 60)
      .line(62)
      .arc(deg(-215), 30)
      .build());
}

/** From the lower left: in, once round a circle, and up. */
export function entrySide(): Path {
  return once('side', () =>
    new PathBuilder(-10, 206, deg(-20))
      .line(56)
      .arc(deg(-430), 28)
      .build());
}

/** Challenging stage: down the left, a three-quarter loop, across the top, round and out to the left. */
function challengeLoop(): Path {
  return once('c/loop', () =>
    new PathBuilder(96, -10, DOWN)
      .line(110)
      .arc(deg(270), 40)
      .line(100)
      .arc(deg(90), 40)
      .line(60)
      .arc(deg(90), 50)
      .line(180)
      .build());
}

/** In low from the left, a full loop, up the right, over, down the middle and out to the left. */
function challengeSweep(): Path {
  return once('c/sweep', () =>
    new PathBuilder(-10, 180, 0)
      .line(60)
      .arc(deg(-360), 28)
      .line(60)
      .arc(deg(-90), 60)
      .line(30)
      .arc(deg(-180), 30)
      .line(60)
      .arc(deg(90), 40)
      .line(110)
      .build());
}

/** Down the left edge, under, up the middle, over, down the right and out. */
function challengeWave(): Path {
  return once('c/wave', () =>
    new PathBuilder(40, -10, DOWN)
      .line(150)
      .arc(deg(-180), 36)
      .line(60)
      .arc(deg(180), 30)
      .line(90)
      .arc(deg(-90), 30)
      .line(50)
      .build());
}

/** Down the middle: a loop one way, a loop the other, then away to the lower left. */
function challengeEight(): Path {
  return once('c/eight', () =>
    new PathBuilder(112, -10, DOWN)
      .line(60)
      .arc(deg(360), 30)
      .line(50)
      .arc(deg(-360), 30)
      .line(40)
      .arc(deg(60), 80)
      .line(140)
      .build());
}

/** The challenging stages' four figures, by name. */
export const CHALLENGE_PATHS = {
  loop: challengeLoop,
  sweep: challengeSweep,
  wave: challengeWave,
  eight: challengeEight,
} as const;

export type ChallengeFigure = keyof typeof CHALLENGE_PATHS;

/** A challenging-stage figure, on its own side or the other. */
export function challengePath(figure: ChallengeFigure, mirror: boolean): Path {
  const p = CHALLENGE_PATHS[figure]();
  return mirror ? mirrored(`c/${figure}`, p) : p;
}

/** An entry path by where it starts. */
export function entryPath(from: 'top' | 'side', mirror: boolean): Path {
  const p = from === 'top' ? entryTop() : entrySide();
  return mirror ? mirrored(from, p) : p;
}

/** Every authored path with a name, for the tests and the path viewer. */
export function allPaths(): { name: string; path: Path }[] {
  const out: { name: string; path: Path }[] = [];
  for (const from of ['top', 'side'] as const) {
    out.push({ name: `entry ${from}`, path: entryPath(from, false) }, { name: `entry ${from} mirrored`, path: entryPath(from, true) });
  }
  for (const figure of Object.keys(CHALLENGE_PATHS) as ChallengeFigure[]) {
    out.push({ name: `challenge ${figure}`, path: challengePath(figure, false) });
    out.push({ name: `challenge ${figure} mirrored`, path: challengePath(figure, true) });
  }
  return out;
}
